import React, { useState } from 'react';
import { useGetHomeDashboardQuery, useLazyGetHomeRequestDetailQuery } from '../../api/homeV3Api';
import { DashboardError } from './components/DashboardError';
import { DashboardHeader } from './components/DashboardHeader';
import { DashboardSkeleton } from './components/DashboardSkeleton';
import { ChartCard } from './components/ChartCard';
import { ExercisePanel } from './components/ExercisePanel';
import { KpiRibbon } from './components/KpiRibbon';
import { RecentRequests } from './components/RecentRequests';
import { RequestPulseChart } from './components/RequestPulseChart';
import { RequestStatusDonut } from './components/RequestStatusDonut';
import { RequestDetailDrawer } from './components/RequestDetailDrawer';
import { TargetingProgressChart } from './components/TargetingProgressChart';
import { useDashboardFilters } from './hooks/useDashboardFilters';
import styles from './HomeV3.module.css';

export default function HomeV3() {
    const { data, isLoading, isError, isFetching, refetch } = useGetHomeDashboardQuery();
    const [selectedRequestKey, setSelectedRequestKey] = useState<string | null>(null);
    const [loadDetail, detailQuery] = useLazyGetHomeRequestDetailQuery();
    const filters = useDashboardFilters(data?.recentRequests ?? []);

    if (isLoading && !data) return <DashboardSkeleton />;
    if (isError && !data) return <DashboardError onRetry={() => void refetch()} />;
    if (!data) return null;

    const focusSection = (id: string) => {
        window.requestAnimationFrame(() => document.getElementById(id)?.focus());
    };

    const openRequest = (itemKey: string) => {
        setSelectedRequestKey(itemKey);
        void loadDetail(itemKey, true);
    };

    const selectedDetail = detailQuery.data?.itemKey === selectedRequestKey ? detailQuery.data : undefined;

    // The exercise panels are omitted entirely when the user's team has no
    // exercise in progress, rather than rendering an empty shell. GetExercises
    // already scopes the list to the caller's team, so an empty array means
    // "nothing open for this team" and there is nothing worth showing.
    const hasExercises = data.exercises.length > 0;

    return (
        <main className={`${styles.dashboard} ${isFetching ? styles.refreshing : ''}`} aria-labelledby="home-dashboard-title">
            <DashboardHeader dashboard={data} isFetching={isFetching} onRefresh={() => void refetch()} />
            <KpiRibbon dashboard={data} onSelectStatus={filters.setStatus} onFocusSection={focusSection} />
            <div className={styles.grid}>
                <ChartCard
                    id="request-pulse"
                    title="Request pulse"
                    eyebrow="Last 30 days"
                    summary="Submitted and completed request activity by day."
                    className={styles.pulsePanel}
                >
                    <RequestPulseChart points={data.requestSummary.dailyActivity} />
                </ChartCard>
                <ChartCard
                    id="request-mix"
                    title="Request mix"
                    eyebrow="Current workload"
                    summary="Select a status to filter recent change requests."
                    className={styles.chartPanel}
                >
                    <RequestStatusDonut buckets={data.requestSummary.statusBuckets} selectedStatus={filters.status} onSelectStatus={filters.setStatus} />
                </ChartCard>
                {hasExercises && (
                    <ChartCard
                        id="targeting-progress"
                        title="Targeting progress"
                        eyebrow="Active exercises"
                        summary="Schedule progress only; this does not represent record completion."
                        className={styles.requestsPanel}
                    >
                        <TargetingProgressChart exercises={data.exercises} />
                    </ChartCard>
                )}
                {hasExercises && <ExercisePanel exercises={data.exercises} />}
                <RecentRequests
                    requests={filters.filteredRequests}
                    totalCount={filters.matchingCount}
                    status={filters.status}
                    showAll={filters.showAll}
                    onStatusChange={filters.setStatus}
                    onToggleAll={() => filters.setShowAll(!filters.showAll)}
                    onOpen={openRequest}
                />
            </div>
            <RequestDetailDrawer
                open={selectedRequestKey !== null}
                detail={selectedDetail}
                isLoading={detailQuery.isFetching}
                onClose={() => setSelectedRequestKey(null)}
            />
        </main>
    );
}
